import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const StructuredData = () => {
  const location = useLocation();
  const { t } = useTranslation();
  
  useEffect(() => {
    // Remove existing breadcrumb schema if present
    const existingBreadcrumb = document.querySelector('script[data-schema="breadcrumb"]');
    if (existingBreadcrumb) {
      existingBreadcrumb.remove();
    }
    
    // Create BreadcrumbList schema based on current route
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [] as Array<{
        "@type": string;
        position: number;
        name: string;
        item?: string;
      }>
    };
    
    // Always add home
    breadcrumbSchema.itemListElement.push({
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://guestlyhomes.com/"
    });
    
    // Add current page if not home
    if (location.pathname === '/corporate-calculator') {
      breadcrumbSchema.itemListElement.push({
        "@type": "ListItem",
        position: 2,
        name: "Corporate Housing Calculator"
      });
    }
    
    // Only add breadcrumb if there's more than just home
    if (breadcrumbSchema.itemListElement.length > 1) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'breadcrumb');
      script.textContent = JSON.stringify(breadcrumbSchema);
      document.head.appendChild(script);
    }
    
    // Add VideoObject schema for testimonials on home page
    if (location.pathname === '/') {
      const existingVideo = document.querySelector('script[data-schema="video"]');
      if (existingVideo) {
        existingVideo.remove();
      }
      
      const testimonials = t('videoTestimonials.testimonials', { returnObjects: true }) as Array<{
        name: string;
        role: string;
        company: string;
        quote: string;
        videoUrl: string;
      }>;
      
      // Only add VideoObject if testimonials have actual video URLs
      testimonials.forEach((testimonial, index) => {
        if (testimonial.videoUrl) {
          const videoSchema = {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            "name": `${testimonial.name} - ${testimonial.company} Testimonial`,
            "description": testimonial.quote,
            "thumbnailUrl": `https://placehold.co/600x450/e2e8f0/64748b?text=${testimonial.name.split(' ')[0]}`,
            "uploadDate": "2024-01-01T00:00:00Z",
            "contentUrl": testimonial.videoUrl,
            "embedUrl": testimonial.videoUrl,
            "duration": "PT2M",
            "publisher": {
              "@type": "Organization",
              "name": "Guestly Homes",
              "logo": {
                "@type": "ImageObject",
                "url": "https://guestlyhomes.com/favicon.svg"
              }
            }
          };
          
          const script = document.createElement('script');
          script.type = 'application/ld+json';
          script.setAttribute('data-schema', 'video');
          script.setAttribute('data-video-index', index.toString());
          script.textContent = JSON.stringify(videoSchema);
          document.head.appendChild(script);
        }
      });
    }
    
    return () => {
      // Cleanup on unmount
      const breadcrumb = document.querySelector('script[data-schema="breadcrumb"]');
      if (breadcrumb) breadcrumb.remove();
      
      const videos = document.querySelectorAll('script[data-schema="video"]');
      videos.forEach(video => video.remove());
    };
  }, [location.pathname, t]);
  
  return null;
};

export default StructuredData;
